import {
  closeCurrentDialog,
  closeDialog,
  DataStatus,
  openDialog,
  Separator,
  showNotification,
} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {head, isNil, mergeAll} from 'ramda';
import {isArray, isNilOrEmpty, isString} from 'ramda-adjunct';

import {
  useGetDirectSaleQuery,
  useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  ActionType,
  DmsDataGrid,
  ItemData,
  ItemInformation,
  PriceListExternalFilters,
  PriceListRowData,
  WAREHOUSE_GRID_CODES,
  WarehouseAvailability,
  WarehouseAvailabilityQueryModifier,
  catchUnhandledDataGridActions,
  handleApiError,
  logError,
} from '@dms/shared';
import {ArticleCreate, PriceListDetail} from '@dms/shared-aftersales';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {TestIdProps, useLatestValue} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const PRICE_LIST_EXTERNAL_FILTER_ID = 'price-list-external-filter';
const PREDEFINED_COLUMNS = [
  'name',
  'catalogNumber',
  'orderingNumber',
  'manufacturerId',
  'supplierId',
  'purchasePrice',
  'recommendedPrice',
  'supplierDiscountGroup',
  'supplierOrderingNumber',
  'supplierManufacturerName',
  'supplierManufacturerCode',
  'standardOrderQuantity',
  'localStock',
  'totalStock',
] as const;

interface PriceListTabProps extends TestIdProps {
  onWarehouseRequest: (warehouseArticleId: string) => Promise<void>;
  onWarehouseNonBinding: (warehouseArticleId: string) => Promise<void>;
  onWarehouseDispatch: (warehouseArticleId: string) => Promise<void>;
  hasPendingRequest: boolean;
}

export function RequestTabMaterialPriceListTab(props: PriceListTabProps) {
  const {directSaleId} = useWarehouseParams();

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  const [getArticleIdByManufacturerNumberAndManufacturerId] =
    useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery();

  const {
    data: directSale,
    isLoading: isDirectSaleLoading,
    isError: hasDirectSaleError,
  } = useGetDirectSaleQuery({directSaleId}, {skip: isNilOrEmpty(directSaleId)});

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([
      filter,
      {branchId: directSale?.branchId, authorizationProfileId: directSale?.authorizationProfileId},
    ]);

  const handleSendToBasket = (articleId: string, actionKey: ActionType) => {
    if (actionKey === 'request') {
      props.onWarehouseRequest(articleId);
    }
    if (actionKey === 'nonBinding') {
      props.onWarehouseNonBinding(articleId);
    }
    if (actionKey === 'dispatch') {
      props.onWarehouseDispatch(articleId);
    }

    closeCurrentDialog();
  };

  const handleOpenDialog = (
    itemInformation: ItemInformation,
    queryModifier: WarehouseAvailabilityQueryModifier
  ) => {
    const handleOnItemClick = async (item: ItemData) => {
      const articleIdResponse = await getArticleIdByManufacturerNumberAndManufacturerId({
        warehouseId: item.warehouseId,
        manufacturerNumber: item.catalogNumber,
        manufacturerId: item.manufacturerId,
      })
        .unwrap()
        .catch(handleApiError);

      const articleId = articleIdResponse?.articleId;

      if (articleId) {
        handleSendToBasket(articleId, item.actionKey);
        return;
      }

      openDialog(
        <ArticleCreate
          defaultValues={{
            warehouseId: item.warehouseId,
            catalogNumber: item.catalogNumber,
            manufacturerId: item.manufacturerId,
            name: itemInformation.name,
          }}
          hasAddToBasketButton
          onAddToBasket={(articleId) => {
            handleSendToBasket(articleId, item.actionKey);

            closeCurrentDialog();
          }}
          onArticleCreate={(createdArticleData) => {
            handleSendToBasket(createdArticleData.articleId, item.actionKey);

            closeCurrentDialog();
          }}
          onClose={() => closeDialog('createArticleDialog')}
          data-testid={testIds.warehouse.priceList('dialogs.createArticle')}
        />,
        {
          id: 'createArticleDialog',
          title: i18n.t('entity.warehouse.labels.createNewArticle'),
          withAdditionalFooter: true,
          scrollBehavior: 'outside',
        }
      );
    };

    openDialog(
      <WarehouseAvailability
        onItemClick={handleOnItemClick}
        itemInformation={itemInformation}
        queryModifier={queryModifier}
      />,
      {
        id: 'warehouseAvailabilityDialog',
        title: i18n.t('entity.warehouse.labels.warehouseAvailability'),
        scrollBehavior: 'outside',
        size: 'large',
      }
    );
  };

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const priceListId = isArray(rowId) ? head(rowId) : rowId;

    const catalogNumber = getColumnData<string>('catalogNumber')?.value;
    const name = getColumnData<string>('name')?.value;
    const manufacturer = getColumnData<{label: string; id: string}>('manufacturerId')?.value;
    const supplier = getColumnData<{label: string; id: string}>('supplierId')?.value;

    if (!isString(catalogNumber)) {
      return logError('Catalog number is not defined');
    }

    if (!isString(name)) {
      return logError('Name is not defined');
    }

    if (!isString(manufacturer?.id)) {
      return logError('Manufacturer id is not defined');
    }

    if (!isString(manufacturer?.label)) {
      return logError('Manufacturer label is not defined');
    }

    if (!isString(supplier?.label)) {
      return logError('Supplier label is not defined');
    }

    if (isNil(priceListId)) {
      return logError('priceListId was not defined');
    }

    const itemInformation: ItemInformation = {
      catalogNumber,
      name,
      manufacturerLabel: manufacturer.label,
      supplierLabel: supplier.label,
    };

    const queryModifier: WarehouseAvailabilityQueryModifier = {
      variantId: directSale?.directSaleVariantId as string,
      originObjectType: directSale?.originObjectType as string,
      manufacturerId: manufacturer.id,
      catalogNumber,
    };

    const priceListRowData = {
      name: {value: getColumnData('name')?.value},
      catalogNumber: {value: getColumnData('catalogNumber')?.value},
      supplierOrderingNumber: {value: getColumnData('supplierOrderingNumber')?.value},
      manufacturerId: {value: getColumnData('manufacturerId')?.value},
      supplierId: {value: getColumnData('supplierId')?.value},
      purchasePrice: {value: getColumnData('purchasePrice')?.value},
      recommendedPrice: {value: getColumnData('recommendedPrice')?.value},
      supplierDiscountGroup: getColumnData('supplierDiscountGroup')?.value,
      supplierManufacturerName: getColumnData('supplierManufacturerName')?.value,
      supplierManufacturerCode: getColumnData('supplierManufacturerCode')?.value,
      standardOrderQuantity: getColumnData('standardOrderQuantity')?.value,
      localStock: getColumnData('localStock')?.value,
      totalStock: getColumnData('totalStock')?.value,
    } as PriceListRowData;

    match(actionKey)
      .with('createArticle', () => {
        handleOpenDialog(itemInformation, queryModifier);
      })
      .with('redirectDetail', () => {
        openDialog(
          <PriceListDetail
            id={priceListId}
            priceListRowData={priceListRowData}
            data-testid={testIds.warehouse.priceList('dialogs.detail')}
          />,
          {
            id: 'detailDialog',
            title: priceListRowData?.name?.value,
            size: 'large',
            withAdditionalFooter: true,
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <DataStatus isLoading={isDirectSaleLoading} isError={hasDirectSaleError} minHeight={100}>
      <HStack height="100%" data-testid={props['data-testid']}>
        <Box flex={1}>
          <VStack height="100%">
            <Box>
              <div id={PRICE_LIST_EXTERNAL_FILTER_ID}></div>
            </Box>
          </VStack>
        </Box>

        <Separator orientation="vertical" />

        <Box flex={3}>
          <DmsDataGrid
            // DG must be re-rendered on folder change to update the query modifier
            gridCode={WAREHOUSE_GRID_CODES.supplierPriceList}
            externalFilterId={PRICE_LIST_EXTERNAL_FILTER_ID}
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'NATIVE'}}
            filterComponent={PriceListExternalFilters}
            data-testid={WAREHOUSE_GRID_CODES.supplierPriceList}
            emptyState={{
              headline: i18n.t('page.warehouseDetail.labels.emptyAssortment'),
            }}
            queryModifier={queryModifier}
          />
        </Box>
      </HStack>
    </DataStatus>
  );
}
